/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { Box, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { HistoricalEquity, EquityFieldKey, LegacyEquity } from '../../../types';
import {
  ehHistoryScannerColumnOrderState,
  ehHistoryScannerColumnsVisibilityState,
  ehHistoryScannerFilterModelState,
  equityHistoryState,
  selectedEquitySymbolState,
} from '../../../states/equityhub';
import useHistory from '../../../hooks/equityhub/useHistory';
import { alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import {
  EquityTableType,
  useEquityGridColumns,
} from './useEquityScannerGridColumns';
import {
  DataGridPremium,
  gridClasses,
  GridColDef,
  GridColumnOrderChangeParams,
  GridFilterModel,
  GridSlots,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@spotgamma/x-data-grid-premium';
import { scannerUnmodifiableFields, historyFields } from '../../../config';
import { useSetSym } from '../../../hooks';
import {
  dayjs,
  getDateFormatted,
  getUpdatedColumnOrder,
  nextBusinessDay,
} from '../../../util';
import { Loader } from '../../shared';
import { EmptyGridOverlay } from '../../stock_scanner/EmptyGridOverlay';
import { EmptyResultsOverlay } from '../../stock_scanner/EmptyResultsOverlay';
import { useQuery } from '@tanstack/react-query';
import useSynthOi from 'hooks/equityhub/useSynthOi';

interface CustomToolbarProps {
  setFilterButtonEl: React.Dispatch<
    React.SetStateAction<HTMLButtonElement | null>
  >;
  selectedSymbol?: string;
}

const CustomToolbar = ({ setFilterButtonEl }: CustomToolbarProps) => {
  const apiRef = useGridApiContext();

  const sxProps: SxProps<Theme> = {
    textTransform: 'capitalize',
    fontSize: {
      xs: 12,
      md: 14,
    },
  };

  const { getSym } = useSetSym();

  const fileName = `${
    getSym() ? `${getSym()}_` : ''
  }history_table_${dayjs().format('YYYY-MM-DD')}`;

  return (
    <GridToolbarContainer sx={{ padding: '5px' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        width="100%"
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <GridToolbarFilterButton
            slotProps={{
              button: {
                sx: sxProps,
              },
            }}
            ref={setFilterButtonEl}
          />
          <GridToolbarColumnsButton
            slotProps={{
              button: {
                sx: sxProps,
              },
            }}
          />
          <GridToolbarExport
            slotProps={{
              button: {
                sx: sxProps,
              },
            }}
            csvOptions={{
              getRowsToExport: () => apiRef.current.getAllRowIds(),
              fileName,
            }}
            excelOptions={{
              getRowsToExport: () => apiRef.current.getAllRowIds(),
              fileName,
            }}
            printOptions={{ disableToolbarButton: true }}
          />
        </Stack>
      </Stack>
    </GridToolbarContainer>
  );
};

export const HistoryTable = () => {
  const [data, setData] = useRecoilState(equityHistoryState);
  const selectedSymbol = useRecoilValue(selectedEquitySymbolState);
  const [dpiRange, setDPIRange] = useState<number[] | undefined>(undefined);
  const [columnVisibilityModel, setColumnVisibilityModel] = useRecoilState(
    ehHistoryScannerColumnsVisibilityState,
  );
  const [filterModel, setFilterModel] = useRecoilState(
    ehHistoryScannerFilterModelState,
  );

  const [columnOrder, setColumnOrder] = useRecoilState(
    ehHistoryScannerColumnOrderState,
  );
  const { isSynthOI } = useSynthOi();

  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const { getHistory } = useHistory();
  const theme = useTheme();

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['equityHistory', selectedSymbol, isSynthOI],
    queryFn: async () => {
      if (!selectedSymbol) {
        return [];
      }
      return await getHistory(selectedSymbol);
    },
    enabled: !!selectedSymbol,
  });

  const { columns, columnGroups } = useEquityGridColumns(
    theme,
    dpiRange,
    EquityTableType.History,
    isSynthOI,
  );

  const defaultContentColor = theme.palette.getContrastText(
    theme.palette.background.default,
  );

  useEffect(() => {
    if (historyData && historyData.length > 0) {
      const dpiArray = historyData.map((d: LegacyEquity) => d.dpi);
      const newDpiRange = [Math.min(...dpiArray), Math.max(...dpiArray)];
      setDPIRange(newDpiRange);

      setData(
        historyData.map((he: any) => ({
          ...he,
          quote_date: getDateFormatted(
            nextBusinessDay(dayjs.utc(he.quote_date)),
          ),
          id: getDateFormatted(nextBusinessDay(dayjs.utc(he.quote_date))),
        })) as HistoricalEquity[],
      );
    }
  }, [historyData, setData]);

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter(
        (column) =>
          !scannerUnmodifiableFields.includes(column.field as EquityFieldKey),
      )
      .map((column) => column.field);
  };

  const orderedColumns = useMemo(() => {
    const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
    const fallback = new Map(
      historyFields.map((field, idx) => [
        field as string,
        idx + columns.length,
      ]),
    );

    return [...columns].sort(
      (a, b) =>
        (orderMap.get(a.field as EquityFieldKey) ?? fallback.get(a.field)!) -
        (orderMap.get(b.field as EquityFieldKey) ?? fallback.get(b.field)!),
    );
  }, [columnOrder, columns]);

  const handleColumnOrderChange = (
    params: GridColumnOrderChangeParams,
    _event: any,
    _details: any,
  ) => {
    const newOrder = getUpdatedColumnOrder(orderedColumns, params);
    setColumnOrder(newOrder as EquityFieldKey[]);
  };

  return (
    <Box
      sx={{
        padding: 2,
        height: '100%',
      }}
    >
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        <DataGridPremium
          rows={data}
          columns={orderedColumns}
          onColumnOrderChange={handleColumnOrderChange}
          columnGroupingModel={columnGroups}
          initialState={{ pinnedColumns: { left: ['trade_date'] } }}
          hideFooter
          disableDensitySelector
          disableMultipleRowSelection
          disableAggregation
          disableRowGrouping
          disableRowSelectionOnClick
          rowHeight={40}
          columnHeaderHeight={52}
          columnVisibilityModel={columnVisibilityModel}
          filterModel={filterModel}
          onColumnVisibilityModelChange={(newModel) => {
            setColumnVisibilityModel(newModel);
          }}
          onFilterModelChange={(model: GridFilterModel) =>
            setFilterModel(model)
          }
          slots={{
            toolbar: CustomToolbar as GridSlots['toolbar'],
            noRowsOverlay: EmptyGridOverlay,
            noResultsOverlay: EmptyResultsOverlay,
          }}
          slotProps={{
            columnsManagement: {
              getTogglableColumns,
            },
            basePopper: {
              sx: {
                '& .MuiSvgIcon-root': {
                  color: alpha(defaultContentColor, 0.75),
                },
              },
            },
            panel: {
              anchorEl: filterButtonEl,
              sx: {
                width: '300px',
                transform: '0px',
              },
            },
            toolbar: {
              setFilterButtonEl,
            },
            filterPanel: {
              filterFormProps: {
                // Customize inputs by passing props
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                },
              },
              sx: {
                gap: 2,

                '& .MuiButtonBase-root': {
                  textTransform: 'capitalize',
                },
                '& .MuiDataGrid-filterForm': { p: 3, mt: 6 },
                '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
                '& .MuiDataGrid-filterFormColumnInput': { mr: 2 },
                '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
              },
            },
          }}
          sx={{
            borderRadius: 2,
            backgroundColor: theme.palette.background.default,
            border: 'none',
            '& .MuiDataGrid-pinnedRows': {
              backgroundColor: theme.palette.scannerGrid.pinnedRow,
              '& .MuiDataGrid-pinnedColumns': {
                backgroundColor: theme.palette.scannerGrid.pinnedRow,
              },
            },
            '& .MuiDataGrid-filler': {
              display: 'none',
            },
            '& .grid-header-cell': {
              backgroundColor: theme.palette.scannerGrid.mainHeader,
              ':hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.1),
              },
              border: 'none',
            },
            '& .MuiDataGrid-topContainer': {
              border: 'none',
            },
            '& .MuiDataGrid-scrollbar': {
              background: theme.palette.background.default,
            },
            '& .MuiDataGrid-pinnedColumns': {
              backgroundColor: theme.palette.scannerGrid.pinnedColumn,
            },
            '& .MuiDataGrid-row:nth-of-type(odd):not(.Mui-selected):not(:hover)':
              {
                backgroundColor: alpha(theme.palette.text.primary, 0.05),
              },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.1),
            },
            '& .custom-group-header': {
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'center',
                height: '40px',
              },
            },
            '& .MuiDataGrid-columnHeader': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              border: 'none',
            },
            '& .MuiDataGrid-sortIcon': {
              color: defaultContentColor,
            },
            '& .MuiDataGrid-menuIconButton': {
              color: defaultContentColor,
            },
            '& .MuiDataGrid-filterIcon': {
              color: defaultContentColor,
            },
            '& .MuiDataGrid-menuIconButton:hover': {
              backgroundColor: theme.palette.background.paper,
              color: alpha(defaultContentColor, 0.5),
            },
            '& .MuiDataGrid-iconSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-cell': {
              color: defaultContentColor,
            },
            '& .MuiDataGrid-row .MuiDataGrid-cell': {
              border: 'none',
            },
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
              {
                outline: 'none',
              },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
              {
                outline: 'none',
              },
          }}
        />
      )}
    </Box>
  );
};
